import {
  requireWorkflowPayloadVersion,
  WorkflowActionTrigger,
  WorkflowCapabilityReference,
  WorkflowDeclaredAction,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';

export interface AutomationField {
  name: string;
  type: string;
  required: boolean;
  nullable: boolean;
  maxLength?: number | null;
  values?: string[];
  items?: AutomationField[];
  maxItems?: number | null;
  recordTypeCode?: string | null;
}
export interface AutomationSchema {
  code: string;
  version: number;
  hash: string;
  fields: AutomationField[];
}
export interface AutomationOperation {
  /** Timer-only advisory metadata; the command provider still authorizes the actual target. */
  supportedAssignmentBases?: ('queues' | 'roles')[];
  command: WorkflowCapabilityReference;
  label: string;
  moduleCode: string;
  moduleVersion: number;
  moduleHash: string;
  lifecycle: string;
  availableForNewUse: boolean;
  packPermitted: boolean;
  unavailableReason: string | null;
  input: AutomationSchema;
  output: AutomationSchema;
  outcomes: string[];
  modes: string[];
  automationInputs: string[];
  caller: string;
  automationPermissionCode: string;
  authorizationPolicy: string;
  requiredContext: string[];
  immediateEffects: string[];
  deferredEffects: string[];
  coordination: string;
  requiredCapabilities: WorkflowCapabilityReference[];
}
export interface AutomationContextBinding {
  code: string;
  label: string;
  type: string;
  nullable: boolean;
  maxLength: number | null;
  triggerKinds: string[];
}
export interface WorkflowAutomationCatalog {
  contractVersion: number;
  execution: { supported: boolean; reasonCode: string; message: string };
  operations: AutomationOperation[];
  contextBindings: AutomationContextBinding[];
  triggerKinds: string[];
  failurePolicies: string[];
}

/** Known document formats retain the existing explicit minimum-version upgrade on authoring. */
export function supportsWorkflowAutomation(version: string | null | undefined): boolean {
  return ['1.0', '1.1', '1.2', '1.3', '1.4', '1.5', '1.6', '1.7', '1.8', '1.9', '1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(version ?? '');
}

export function automationOperation(
  catalog: WorkflowAutomationCatalog | null,
  action: WorkflowDeclaredAction | null,
): AutomationOperation | null {
  if (!action?.command?.key) return null;
  const matches = catalog?.operations.filter((operation) =>
      sameCapabilityReference(operation.command, action.command),
    ) ?? [];
  return matches.length === 1 ? matches[0] : null;
}

/** The strict trigger reader cannot retain unknown members through an editable projection. */
export function automationShapeError(value: unknown): string | null {
  if (value == null) return null;
  const object = (value: unknown, keys: readonly string[]): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).every(key => keys.includes(key));
  const optionalText = (value: unknown) => value == null || typeof value === 'string';
  if (!Array.isArray(value) || value.some(trigger => {
    if (!object(trigger, ['id', 'kind', 'nodeId', 'actions', 'edgeId', 'outcomeCode']) ||
      !['id', 'kind', 'nodeId'].every(key => typeof trigger[key] === 'string') ||
      !optionalText(trigger['edgeId']) || !optionalText(trigger['outcomeCode']) || !Array.isArray(trigger['actions'])) return true;
    return trigger['actions'].some((action: unknown) => {
      if (!object(action, ['id', 'typeCode', 'command', 'inputs', 'outcomes', 'group', 'order', 'mode', 'failurePolicy', 'caller', 'requiredForProgression']) ||
        !['id', 'typeCode', 'group'].every(key => typeof action[key] === 'string') || typeof action['order'] !== 'number' ||
        !['mode', 'failurePolicy', 'caller'].every(key => action[key] === undefined || typeof action[key] === 'string') ||
        (action['requiredForProgression'] !== undefined && typeof action['requiredForProgression'] !== 'boolean') ||
        !Array.isArray(action['outcomes']) || action['outcomes'].some(item => typeof item !== 'string') || !Array.isArray(action['inputs'])) return true;
      const command = action['command'];
      if (!object(command, ['key', 'inputSchemaHash', 'outputSchemaHash', 'contractHash']) ||
        !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(key => typeof command[key] === 'string')) return true;
      const key = command['key'];
      if (!object(key, ['provider', 'kind', 'code', 'version']) ||
        !['provider', 'kind', 'code'].every(name => typeof key[name] === 'string') || typeof key['version'] !== 'number') return true;
      return action['inputs'].some((input: unknown) => !object(input, ['field', 'source', 'value', 'context', 'literalJson']) ||
        typeof input['field'] !== 'string' || typeof input['source'] !== 'string' || !optionalText(input['context']) || input['literalJson'] != null);
    });
  })) return 'These saved event declarations contain unsupported content. Their original configuration is retained read-only.';
  return null;
}

export function automationFingerprint(
  document: WorkflowDefinitionDocument,
): string {
  return JSON.stringify({
    workflowCode: document.workflowCode,
    packReference: document.packReference,
    triggers: document.triggers,
    capabilityReferences: document.capabilityReferences,
    nodes: document.nodes,
    edges: document.edges,
  });
}

export function nextAutomationId(
  prefix: string,
  used: readonly string[],
): string {
  let number = 1;
  while (used.includes(`${prefix}-${number}`)) number++;
  return `${prefix}-${number}`;
}

export function compatibleAutomationContexts(
  catalog: WorkflowAutomationCatalog,
  kind: string,
  field: AutomationField,
  document?: Pick<WorkflowDefinitionDocument, 'subjectTypeCode'>,
): AutomationContextBinding[] {
  return catalog.contextBindings.filter(
    (source) =>
      source.triggerKinds.includes(kind) &&
      (source.code !== 'subject.id' || !!document?.subjectTypeCode) &&
      source.type === field.type &&
      (!source.nullable || field.nullable) &&
      !field.values?.length &&
      (field.maxLength == null ||
        (source.maxLength != null && source.maxLength <= field.maxLength)),
  );
}

export function automationLiteralError(
  field: AutomationField,
  value: unknown,
): string | null {
  if (value === null) return field.nullable ? null : 'A value is required.';
  if (value === undefined)
    return field.required ? 'A value is required.' : null;
  switch (field.type) {
    case 'string':
      return typeof value !== 'string' ||
        (field.maxLength != null && value.length > field.maxLength) ||
        (field.values?.length && !field.values.includes(value))
        ? 'Choose a value matching the declared text contract.'
        : null;
    case 'integer':
      return typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= -2147483648 &&
        value <= 2147483647
        ? null
        : 'Enter a whole number in the supported range.';
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? null
        : 'Enter a finite number.';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'Choose Yes or No.';
    case 'array':
      if (
        !Array.isArray(value) ||
        field.maxItems == null ||
        value.length > field.maxItems
      )
        return 'Supply a bounded list matching the declared fields.';
      for (const row of value) {
        if (
          !row ||
          typeof row !== 'object' ||
          Array.isArray(row) ||
          Object.keys(row).some(
            (key) => !field.items?.some((item) => item.name === key),
          )
        )
          return 'List rows must contain only the declared fields.';
        for (const item of field.items ?? []) {
          const error = automationLiteralError(
            item,
            (row as Record<string, unknown>)[item.name],
          );
          if (error) return `${item.name}: ${error}`;
        }
      }
      return null;
    default:
      return 'This input type is unavailable.';
  }
}

export function automationEdges(
  document: WorkflowDefinitionDocument,
  nodeId: string,
) {
  const direct = document.edges.filter((edge) => edge.fromNodeId === nodeId);
  const routers = direct
    .map((edge) => edge.toNodeId)
    .filter((id) =>
      document.nodes.some((node) => node.id === id && !!node.routing),
    );
  return document.edges.filter(
    (edge) => edge.fromNodeId === nodeId || routers.includes(edge.fromNodeId),
  );
}

export function applyWorkflowAutomation(
  document: WorkflowDefinitionDocument,
  triggers: WorkflowActionTrigger[],
  catalog: WorkflowAutomationCatalog,
  original: WorkflowActionTrigger[],
): WorkflowDefinitionDocument {
  const fail = (message: string): never => {
    throw new Error(message);
  };
  const code = (value: string) =>
    /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(value) && value.length <= 120;
  if (!supportsWorkflowAutomation(document.payloadSchemaVersion)) fail('This workflow format is unsupported. Its original event declarations are retained read-only.');
  if (catalog.contractVersion !== 1)
    fail('Reload the supported automation catalog before applying changes.');
  const shapeError = automationShapeError(triggers);
  if (shapeError) fail(shapeError);
  if (triggers.length > 64)
    fail('A workflow supports at most 64 automated triggers.');
  const triggerIds = new Set<string>(),
    actionIds = new Set<string>();
  const references = [...(document.capabilityReferences ?? [])];
  for (const trigger of triggers) {
    if (!code(trigger.id) || triggerIds.has(trigger.id))
      fail('Trigger identities must be unique.');
    triggerIds.add(trigger.id);
    const retained = original.find((item) => item.id === trigger.id);
    if (
      !catalog.triggerKinds.includes(trigger.kind) ||
      !document.nodes.some(
        (node) => node.id === trigger.nodeId && node.typeCode === 'human_step',
      )
    )
      fail(`Choose a supported event and work step for ${trigger.id}.`);
    const edges = automationEdges(document, trigger.nodeId);
    if (trigger.kind === 'transition' || trigger.kind === 'return') {
      if (
        !edges.some(
          (edge) =>
            edge.id === trigger.edgeId &&
            (trigger.kind !== 'return' || edge.pathKindCode === 'return'),
        )
      )
        fail(`Choose a current path for ${trigger.id}.`);
    } else if (trigger.edgeId != null)
      fail(`The ${trigger.kind} event cannot select a path.`);
    if (trigger.kind === 'decision') {
      if (
        !edges.some(
          (edge) =>
            edge.fromNodeId !== trigger.nodeId &&
            edge.outcomeCode === trigger.outcomeCode,
        )
      )
        fail(`Choose a current decision outcome for ${trigger.id}.`);
    } else if (trigger.outcomeCode != null)
      fail(`The ${trigger.kind} event cannot select a decision outcome.`);
    if (!trigger.actions.length || trigger.actions.length > 32)
      fail('Each trigger needs one to 32 actions.');
    for (const action of trigger.actions) {
      if (!code(action.id) || actionIds.has(action.id) || actionIds.size >= 256)
        fail(
          'Action identities must be unique, with at most 256 per workflow.',
        );
      actionIds.add(action.id);
      const operation = automationOperation(catalog, action);
      if (!operation || !operation.availableForNewUse) {
        const old = retained?.actions.find((item) => item.id === action.id);
        if (
          !old ||
          JSON.stringify(old) !== JSON.stringify(action) ||
          retained?.nodeId !== trigger.nodeId ||
          retained.kind !== trigger.kind ||
          retained.edgeId !== trigger.edgeId ||
          retained.outcomeCode !== trigger.outcomeCode
        )
          fail(
            'An unavailable retained operation cannot be changed or copied. Restore its exact installed contract, or remove it.',
          );
        continue;
      }
      if (
        action.typeCode !== 'command' ||
        action.caller !== 'scoped-system' ||
        action.requiredForProgression ||
        !operation.modes.includes(action.mode) ||
        !catalog.failurePolicies.includes(action.failurePolicy) ||
        !code(action.group) ||
        !Number.isInteger(action.order) ||
        action.order < 0 ||
        action.order > 31
      )
        fail(`Review the execution settings for ${action.id}.`);
      if (
        [...action.outcomes].sort().join('|') !==
        [...operation.outcomes].sort().join('|')
      )
        fail(`Restore the registered outcomes for ${action.id}.`);
      const seen = new Set<string>();
      for (const binding of action.inputs) {
        const field = operation.input.fields.find(
          (item) => item.name === binding.field,
        );
        if (
          !field ||
          !operation.automationInputs.includes(binding.field) ||
          seen.has(binding.field)
        )
          fail(`Review the declared inputs for ${action.id}.`);
        seen.add(binding.field);
        if (binding.source === 'literal') {
          const error = automationLiteralError(field!, binding.value);
          if (error || binding.context != null)
            fail(`${binding.field}: ${error ?? 'Choose one input source.'}`);
        } else if (
          binding.source !== 'context' ||
          binding.value != null ||
          !compatibleAutomationContexts(
            catalog,
            trigger.kind,
            field!,
            document,
          ).some((item) => item.code === binding.context)
        )
          fail(`Choose a compatible context binding for ${binding.field}.`);
      }
      if (
        operation.input.fields.some(
          (field) => field.required && !seen.has(field.name),
        )
      )
        fail(`Supply every required input for ${action.id}.`);
      for (const reference of operation.requiredCapabilities) {
        const existing = references.filter(
          (ref) =>
            capabilityIdentity(ref.key) === capabilityIdentity(reference.key),
        );
        if (
          existing.length > 1 ||
          existing.some((ref) => !sameCapabilityReference(ref, reference))
        )
          fail(
            'A saved capability reference has changed meaning. Repair it before applying automation.',
          );
        if (!existing.length) references.push(structuredClone(reference));
      }
    }
    for (const group of new Set(
      trigger.actions.map((action) => action.group),
    )) {
      const rows = trigger.actions.filter((action) => action.group === group);
      if (
        new Set(rows.map((row) => row.order)).size !== rows.length ||
        new Set(rows.map((row) => row.failurePolicy)).size !== 1 ||
        (rows[0].failurePolicy === 'stop' &&
          new Set(rows.map((row) => row.mode)).size !== 1)
      )
        fail(
          'Actions in a group need unique order, one failure policy and one mode when stopping on failure.',
        );
    }
  }
  if (references.length > 100)
    fail('A workflow supports at most 100 capability references.');
  return {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.5',
    ),
    triggers: structuredClone(triggers),
    capabilityReferences: references,
  };
}
